import { Loader2, Check, AlertCircle } from 'lucide-preact'
import { BRAND_CLASSES } from '@/constants/theme'

export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

interface SaveStatusIndicatorProps {
    status: SaveStatus
    error: string | null
    onRetry: () => void
}

/**
 * Compact text + icon save-status pill used by every auto-saving editor (Email Copy fields,
 * Email Template canvas, etc.). Renders a single line — caller controls placement.
 */
export function SaveStatusIndicator({ status, error, onRetry }: SaveStatusIndicatorProps) {
    if (status === 'saving') {
        return (
            <span className={`inline-flex items-center gap-1 text-[11px] ${BRAND_CLASSES.INPUT.HELPER}`}>
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving…
            </span>
        )
    }
    if (status === 'dirty') {
        return (
            <span className={`text-[11px] ${BRAND_CLASSES.INPUT.HELPER}`}>
                Unsaved changes
            </span>
        )
    }
    if (status === 'saved') {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                <Check className="w-3 h-3" />
                Saved
            </span>
        )
    }
    if (status === 'error') {
        return (
            <span className={`inline-flex items-center gap-1.5 text-[11px] ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
                <AlertCircle className="w-3 h-3" />
                {error || 'Save failed'}
                <button
                    type="button"
                    onClick={onRetry}
                    className="underline decoration-dotted underline-offset-2 hover:text-red-700 dark:hover:text-red-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
                >
                    Retry
                </button>
            </span>
        )
    }
    return <span className={`text-[11px] ${BRAND_CLASSES.INPUT.HELPER}`}>—</span>
}
